import { useState, useEffect, useRef } from 'react';
import { useCart } from '@/context/CartContext';

interface CheckoutForm {
  name: string;
  phone: string;
  address: string;
  city: string;
}

const WHATSAPP_NUMBER = '923445299984';

const CartDrawer = () => {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalPrice, totalItems, clearCart } =
    useCart();
  const [step, setStep] = useState<'cart' | 'checkout'>('cart');
  const [form, setForm] = useState<CheckoutForm>({ name: '', phone: '', address: '', city: '' });
  const [errors, setErrors] = useState<Partial<CheckoutForm>>({});
  const [orderPlaced, setOrderPlaced] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep('cart');
        setOrderPlaced(false);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeCart]);

  // Prevent body scroll only on desktop; on mobile keep page visible behind drawer
  useEffect(() => {
    const isMobile = window.innerWidth < 640;
    if (!isMobile) {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Handle browser back button: if cart is open, close it instead of navigating
  useEffect(() => {
    if (!isOpen) return;
    // Push a dummy history entry so back button triggers popstate
    window.history.pushState({ cartOpen: true }, '');
    const handlePopState = (e: PopStateEvent) => {
      if (isOpen) {
        e.preventDefault();
        closeCart();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, closeCart]);

  const validate = (): boolean => {
    const errs: Partial<CheckoutForm> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.phone.trim()) errs.phone = 'Contact number is required';
    else if (!/^[0-9+\-\s]{7,15}$/.test(form.phone.trim())) errs.phone = 'Enter a valid phone number';
    if (!form.address.trim()) errs.address = 'Address is required';
    if (!form.city.trim()) errs.city = 'City is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCompleteOrder = () => {
    if (!validate()) return;
    const orderLines = items
      .map((i) => `- ${i.product.name} (${i.size.label}) x${i.quantity} = Rs. ${(i.size.price * i.quantity).toLocaleString()}`)
      .join('\n');
    const message = `Hi Pearl Foods,\n\nI want to place an order:\n\nName: ${form.name}\nPhone: ${form.phone}\nAddress: ${form.address}\nCity: ${form.city}\n\nOrder Details:\n${orderLines}\n\nTotal: Rs. ${totalPrice.toLocaleString()}\n\nPlease confirm availability.`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    clearCart();
    setOrderPlaced(true);
  };

  const handleFormChange = (field: keyof CheckoutForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer — side panel on all screen sizes, never full-screen */}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full z-[70] w-[88vw] max-w-[420px] bg-brand-cream flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping Cart"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-beige flex-shrink-0">
          <div className="flex items-center gap-2">
            {step === 'checkout' && !orderPlaced && (
              <button
                onClick={() => setStep('cart')}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige text-brand-brown hover:bg-brand-beige/70 transition-colors cursor-pointer mr-1"
                aria-label="Back to cart"
              >
                <i className="ri-arrow-left-line text-base"></i>
              </button>
            )}
            <div className="w-5 h-5 flex items-center justify-center text-brand-olive">
              <i className="ri-shopping-basket-2-line text-lg"></i>
            </div>
            <h2 className="font-serif text-brand-brown-dark font-semibold text-lg">
              {orderPlaced ? 'Order Sent!' : step === 'cart' ? `My Cart (${totalItems})` : 'Delivery Details'}
            </h2>
          </div>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige text-brand-brown hover:bg-brand-beige/70 transition-colors cursor-pointer"
            aria-label="Close cart"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {orderPlaced ? (
            <OrderSuccess onClose={closeCart} />
          ) : step === 'cart' ? (
            <CartItems
              items={items}
              removeItem={removeItem}
              updateQuantity={updateQuantity}
              totalPrice={totalPrice}
            />
          ) : (
            <CheckoutFormView
              form={form}
              errors={errors}
              onChange={handleFormChange}
              items={items}
              totalPrice={totalPrice}
            />
          )}
        </div>

        {/* Footer Actions */}
        {!orderPlaced && items.length > 0 && (
          <div className="flex-shrink-0 px-5 py-4 border-t border-brand-beige bg-brand-cream">
            {step === 'cart' ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-sans text-sm text-brand-brown/70">Total</span>
                  <span className="font-serif text-xl font-bold text-brand-brown-dark">
                    Rs. {totalPrice.toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={() => setStep('checkout')}
                  className="w-full flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-olive-dark text-white font-sans font-medium py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Proceed to Checkout
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-sans text-sm text-brand-brown/70">Order Total</span>
                  <span className="font-serif text-xl font-bold text-brand-brown-dark">
                    Rs. {totalPrice.toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={handleCompleteOrder}
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20b858] text-white font-sans font-medium py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </div>
                  Complete Order on WhatsApp
                </button>
                <p className="text-center font-sans text-xs text-brand-brown/50">
                  You'll be redirected to WhatsApp to confirm your order
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

/* ─── Cart Items View ─── */
interface CartItemsProps {
  items: ReturnType<typeof useCart>['items'];
  removeItem: (id: number, label: string) => void;
  updateQuantity: (id: number, label: string, qty: number) => void;
  totalPrice: number;
}

const CartItems = ({ items, removeItem, updateQuantity }: CartItemsProps) => {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-beige mb-4">
          <i className="ri-shopping-basket-2-line text-2xl text-brand-brown/40"></i>
        </div>
        <h3 className="font-serif text-brand-brown-dark font-semibold text-lg mb-2">Your cart is empty</h3>
        <p className="font-sans text-sm text-brand-brown/60">
          Add some pure, handmade products to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="px-5 py-4 space-y-4">
      {items.map((item) => (
        <div
          key={`${item.product.id}-${item.size.label}`}
          className="flex gap-3 bg-white rounded-xl p-3"
        >
          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src={item.product.image}
              alt={item.product.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="font-serif text-sm font-semibold text-brand-brown-dark leading-tight truncate">
                  {item.product.name}
                </h4>
                <span className="font-sans text-xs text-brand-brown/50">{item.size.label}</span>
              </div>
              <button
                onClick={() => removeItem(item.product.id, item.size.label)}
                className="w-5 h-5 flex items-center justify-center text-brand-brown/30 hover:text-red-400 transition-colors cursor-pointer flex-shrink-0"
                aria-label="Remove item"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-0">
                <button
                  onClick={() => updateQuantity(item.product.id, item.size.label, item.quantity - 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/70 text-brand-brown text-sm transition-colors cursor-pointer"
                  aria-label="Decrease quantity"
                >
                  <i className="ri-subtract-line text-xs"></i>
                </button>
                <span className="font-sans text-sm font-medium text-brand-brown-dark w-8 text-center">
                  {item.quantity}
                </span>
                <button
                  onClick={() => updateQuantity(item.product.id, item.size.label, item.quantity + 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/70 text-brand-brown text-sm transition-colors cursor-pointer"
                  aria-label="Increase quantity"
                >
                  <i className="ri-add-line text-xs"></i>
                </button>
              </div>
              <span className="font-serif text-sm font-semibold text-brand-olive">
                Rs. {(item.size.price * item.quantity).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─── Checkout Form View ─── */
interface CheckoutFormViewProps {
  form: CheckoutForm;
  errors: Partial<CheckoutForm>;
  onChange: (field: keyof CheckoutForm, value: string) => void;
  items: ReturnType<typeof useCart>['items'];
  totalPrice: number;
}

const CheckoutFormView = ({ form, errors, onChange, items, totalPrice }: CheckoutFormViewProps) => (
  <div className="px-5 py-4 space-y-5">
    {/* Order Summary */}
    <div className="bg-white rounded-xl p-4">
      <h3 className="font-serif text-sm font-semibold text-brand-brown-dark mb-3">Order Summary</h3>
      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={`${item.product.id}-${item.size.label}`} className="flex justify-between items-center">
            <span className="font-sans text-xs text-brand-brown/70 truncate mr-2">
              {item.product.name} ({item.size.label}) ×{item.quantity}
            </span>
            <span className="font-sans text-xs font-medium text-brand-brown-dark whitespace-nowrap">
              Rs. {(item.size.price * item.quantity).toLocaleString()}
            </span>
          </div>
        ))}
        <div className="border-t border-brand-beige pt-2 mt-2 flex justify-between items-center">
          <span className="font-sans text-xs font-semibold text-brand-brown">Total</span>
          <span className="font-serif text-base font-bold text-brand-olive">
            Rs. {totalPrice.toLocaleString()}
          </span>
        </div>
      </div>
    </div>

    {/* Form Fields */}
    <div>
      <h3 className="font-serif text-sm font-semibold text-brand-brown-dark mb-3">Your Details</h3>
      <div className="space-y-3">
        <div>
          <label className="block font-sans text-xs text-brand-brown/70 mb-1.5">Full Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g. Ahmed Khan"
            className={`w-full font-sans text-sm text-brand-brown-dark placeholder-brand-brown/30 bg-white border rounded-xl px-4 py-3 outline-none transition-colors ${
              errors.name ? 'border-red-400 focus:border-red-400' : 'border-brand-beige focus:border-brand-olive'
            }`}
          />
          {errors.name && (
            <p className="font-sans text-xs text-red-400 mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block font-sans text-xs text-brand-brown/70 mb-1.5">Contact Number *</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="e.g. 0312 1234567"
            className={`w-full font-sans text-sm text-brand-brown-dark placeholder-brand-brown/30 bg-white border rounded-xl px-4 py-3 outline-none transition-colors ${
              errors.phone ? 'border-red-400 focus:border-red-400' : 'border-brand-beige focus:border-brand-olive'
            }`}
          />
          {errors.phone && (
            <p className="font-sans text-xs text-red-400 mt-1">{errors.phone}</p>
          )}
        </div>

        <div>
          <label className="block font-sans text-xs text-brand-brown/70 mb-1.5">Delivery Address *</label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => onChange('address', e.target.value)}
            placeholder="House no., Street, Area"
            className={`w-full font-sans text-sm text-brand-brown-dark placeholder-brand-brown/30 bg-white border rounded-xl px-4 py-3 outline-none transition-colors ${
              errors.address ? 'border-red-400 focus:border-red-400' : 'border-brand-beige focus:border-brand-olive'
            }`}
          />
          {errors.address && (
            <p className="font-sans text-xs text-red-400 mt-1">{errors.address}</p>
          )}
        </div>

        <div>
          <label className="block font-sans text-xs text-brand-brown/70 mb-1.5">City *</label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="e.g. Islamabad"
            className={`w-full font-sans text-sm text-brand-brown-dark placeholder-brand-brown/30 bg-white border rounded-xl px-4 py-3 outline-none transition-colors ${
              errors.city ? 'border-red-400 focus:border-red-400' : 'border-brand-beige focus:border-brand-olive'
            }`}
          />
          {errors.city && (
            <p className="font-sans text-xs text-red-400 mt-1">{errors.city}</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

/* ─── Order Success View ─── */
const OrderSuccess = ({ onClose }: { onClose: () => void }) => (
  <div className="flex flex-col items-center justify-center h-full px-6 py-16 text-center">
    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#25D366]/15 mb-5">
      <i className="ri-whatsapp-line text-2xl text-[#25D366]"></i>
    </div>
    <h3 className="font-serif text-brand-brown-dark font-bold text-xl mb-2">Order Sent!</h3>
    <p className="font-sans text-sm text-brand-brown/70 max-w-xs mb-6">
      Your order has been sent to WhatsApp. Pearl Foods will confirm your order shortly.
    </p>
    <button
      onClick={onClose}
      className="font-sans text-sm font-medium text-brand-olive hover:text-brand-olive-dark transition-colors cursor-pointer"
    >
      Continue Shopping
    </button>
  </div>
);

export default CartDrawer;
